import { create } from 'zustand';
import api from '../lib/api';

// ── Types ────────────────────────────────────────────────────────────────────

export interface PaymentMethod {
  id: string;
  brand: string;
  last4: string;
  expMonth: number;
  expYear: number;
  isDefault: boolean;
}

export interface WalletBalance {
  balance: number;
  currency: string;
}

export interface BillingTransaction {
  id: string;
  user_id: string;
  type: 'game_purchase' | 'hosting_subscription' | 'erozion_plus' | 'wallet_topup' | 'refund';
  status: 'pending' | 'completed' | 'failed' | 'refunded';
  amount: number;
  currency: string;
  wallet_amount: number;
  stripe_amount: number;
  item_type: string | null;
  item_id: string | null;
  item_name: string | null;
  description: string | null;
  receipt_url?: string | null;
  stripe_payment_intent_id?: string | null;
  created_at: string;
  updated_at?: string;
}

export interface SubscriptionPlan {
  id: string;
  type: 'erozion_plus' | 'hosting';
  name: string;
  display_name: string;
  description: string | null;
  price_monthly: number;
  currency: string;
  stripe_price_id: string | null;
  trial_days: number;
  features: string[];
  is_active: boolean;
  is_subscribed: boolean;
}

export interface Subscription {
  id: string;
  user_id: string;
  type: 'erozion_plus' | 'hosting';
  plan_id: string | null;
  plan_name: string | null;
  price_monthly: number | null;
  features: string[] | null;
  stripe_subscription_id: string;
  stripe_price_id: string | null;
  status: string;
  trial_start: string | null;
  trial_end: string | null;
  current_period_start: string;
  current_period_end: string;
  cancel_at_period_end: boolean;
  canceled_at: string | null;
  created_at: string;
}

// ── Payment drawer item ──────────────────────────────────────────────────────

export interface PaymentDrawerItem {
  type: 'game_purchase' | 'wallet_topup' | 'hosting_subscription' | 'erozion_plus';
  itemId?: string;
  itemName: string;
  amount: number;
  currency: string;
  imageUrl?: string | null;
  description?: string;
  /** For subscription plans */
  stripePriceId?: string;
}

// ── Store ────────────────────────────────────────────────────────────────────

interface BillingState {
  // Data
  methods: PaymentMethod[];
  defaultMethodId: string | null;
  wallet: WalletBalance | null;
  transactions: BillingTransaction[];
  transactionsTotal: number;
  subscriptions: Subscription[];

  // UI state
  isLoadingMethods: boolean;
  isLoadingWallet: boolean;
  isLoadingTransactions: boolean;
  isSavingCard: boolean;
  error: string | null;

  // Payment drawer
  drawerOpen: boolean;
  drawerItem: PaymentDrawerItem | null;

  // Actions — payment methods
  fetchMethods: () => Promise<void>;
  removeMethod: (methodId: string) => Promise<boolean>;
  setDefaultMethod: (methodId: string) => Promise<boolean>;
  createSetupIntent: () => Promise<string | null>;

  // Actions — wallet
  fetchWallet: (currency?: string) => Promise<void>;
  createWalletTopup: (amount: number, currency?: string, paymentMethodId?: string, saveCard?: boolean) => Promise<{ clientSecret?: string; completed?: boolean } | null>;

  // Actions — transactions
  fetchTransactions: (offset?: number, type?: string) => Promise<void>;
  fetchTransactionDetail: (txId: string) => Promise<BillingTransaction | null>;

  // Actions — subscriptions
  plans: SubscriptionPlan[];
  isLoadingPlans: boolean;
  fetchPlans: (type?: string) => Promise<void>;
  fetchSubscriptions: () => Promise<void>;
  createSubscription: (planId: string, paymentMethodId?: string) => Promise<{ status?: string; clientSecret?: string; error?: string } | null>;
  cancelSubscription: (subId: string) => Promise<boolean>;
  reactivateSubscription: (subId: string) => Promise<boolean>;

  // Actions — payment drawer
  openPaymentDrawer: (item: PaymentDrawerItem) => void;
  closePaymentDrawer: () => void;

  // Actions — sync from Stripe on login
  syncBilling: () => Promise<void>;

  // Actions — game purchase (new flow)
  initiateGamePurchase: (gameId: string, paymentMethodId?: string, useWallet?: boolean, saveCard?: boolean) => Promise<{ clientSecret?: string; completed?: boolean; failed?: boolean; message?: string } | null>;
}

export const useBillingStore = create<BillingState>((set, get) => ({
  methods: [],
  defaultMethodId: null,
  wallet: null,
  transactions: [],
  transactionsTotal: 0,
  subscriptions: [],
  plans: [],
  isLoadingPlans: false,

  isLoadingMethods: false,
  isLoadingWallet: false,
  isLoadingTransactions: false,
  isSavingCard: false,
  error: null,

  drawerOpen: false,
  drawerItem: null,

  // ── Payment methods ──────────────────────────────────────────────────────

  fetchMethods: async () => {
    set({ isLoadingMethods: true, error: null });
    try {
      const { data } = await api.get('/billing/methods');
      set({
        methods: Array.isArray(data.methods) ? data.methods : [],
        defaultMethodId: data.defaultMethodId ?? null,
      });
    } catch (e: any) {
      set({ error: e.response?.data?.message || 'Erreur de chargement' });
    } finally {
      set({ isLoadingMethods: false });
    }
  },

  removeMethod: async (methodId) => {
    try {
      await api.delete(`/billing/methods/${methodId}`);
      set((s) => ({
        methods: s.methods.filter((m) => m.id !== methodId),
      }));
      return true;
    } catch (e: any) {
      set({ error: e.response?.data?.message || 'Erreur de suppression' });
      return false;
    }
  },

  setDefaultMethod: async (methodId) => {
    try {
      await api.put(`/billing/methods/${methodId}/default`);
      set((s) => ({
        methods: s.methods.map((m) => ({ ...m, isDefault: m.id === methodId })),
        defaultMethodId: methodId,
      }));
      return true;
    } catch (e: any) {
      set({ error: e.response?.data?.message || 'Erreur' });
      return false;
    }
  },

  createSetupIntent: async () => {
    set({ isSavingCard: true, error: null });
    try {
      const { data } = await api.post('/billing/setup-intent');
      return data.clientSecret;
    } catch (e: any) {
      set({ error: e.response?.data?.message || 'Impossible de préparer le formulaire' });
      return null;
    } finally {
      set({ isSavingCard: false });
    }
  },

  // ── Wallet ───────────────────────────────────────────────────────────────

  fetchWallet: async (currency = 'cad') => {
    set({ isLoadingWallet: true });
    try {
      const { data } = await api.get(`/billing/wallet?currency=${currency}`);
      set({ wallet: data });
    } catch (e: any) {
      set({ error: e.response?.data?.message || 'Erreur portefeuille' });
    } finally {
      set({ isLoadingWallet: false });
    }
  },

  createWalletTopup: async (amount, currency = 'cad', paymentMethodId?, saveCard?) => {
    try {
      const body: Record<string, unknown> = { amount, currency };
      if (paymentMethodId) body.paymentMethodId = paymentMethodId;
      if (saveCard !== undefined) body.saveCard = saveCard;
      const { data } = await api.post('/billing/wallet/topup', body);
      if (data.completed) return { completed: true };
      return { clientSecret: data.clientSecret };
    } catch (e: any) {
      set({ error: e.response?.data?.message || 'Erreur de recharge' });
      return null;
    }
  },

  // ── Transactions ─────────────────────────────────────────────────────────

  fetchTransactions: async (offset = 0, type) => {
    set({ isLoadingTransactions: true });
    try {
      const params = new URLSearchParams({ offset: String(offset), limit: '20' });
      if (type) params.set('type', type);
      const { data } = await api.get(`/billing/transactions?${params}`);
      const nextTransactions = Array.isArray(data.transactions) ? data.transactions : [];
      set({
        transactions: offset === 0 ? nextTransactions : [...(get().transactions ?? []), ...nextTransactions],
        transactionsTotal: data.total ?? nextTransactions.length,
      });
    } catch (e: any) {
      set({ error: e.response?.data?.message || 'Erreur' });
    } finally {
      set({ isLoadingTransactions: false });
    }
  },

  fetchTransactionDetail: async (txId) => {
    try {
      const { data } = await api.get(`/billing/transactions/${txId}`);
      return data as BillingTransaction;
    } catch {
      return null;
    }
  },

  // ── Subscriptions ────────────────────────────────────────────────────────

  fetchPlans: async (type) => {
    set({ isLoadingPlans: true });
    try {
      const params = type ? `?type=${type}` : '';
      const { data } = await api.get(`/billing/plans${params}`);
      set({ plans: Array.isArray(data.plans) ? data.plans : [] });
    } catch {
      // silent
    } finally {
      set({ isLoadingPlans: false });
    }
  },

  fetchSubscriptions: async () => {
    try {
      const { data } = await api.get('/billing/subscriptions');
      set({ subscriptions: Array.isArray(data.subscriptions) ? data.subscriptions : [] });
    } catch {
      // silent
    }
  },

  createSubscription: async (planId, paymentMethodId) => {
    try {
      const { data } = await api.post('/billing/subscriptions', { planId, paymentMethodId });
      // Refresh subscriptions list
      get().fetchSubscriptions();
      get().fetchPlans();
      return { status: data.status, clientSecret: data.clientSecret };
    } catch (e: any) {
      const msg = e.response?.data?.message || 'Erreur lors de la souscription';
      return { error: msg };
    }
  },

  cancelSubscription: async (subId) => {
    try {
      await api.post(`/billing/subscriptions/${subId}/cancel`);
      get().fetchSubscriptions();
      return true;
    } catch {
      return false;
    }
  },

  reactivateSubscription: async (subId) => {
    try {
      await api.post(`/billing/subscriptions/${subId}/reactivate`);
      get().fetchSubscriptions();
      return true;
    } catch {
      return false;
    }
  },

  // ── Payment drawer ─────────────────────────────────────────────────────

  openPaymentDrawer: (item) => {
    set({ drawerOpen: true, drawerItem: item });
  },

  closePaymentDrawer: () => {
    set({ drawerOpen: false, drawerItem: null });
  },

  syncBilling: async () => {
    try {
      await api.post('/billing/sync');
      // After sync, refresh local data
      await Promise.all([
        get().fetchSubscriptions(),
        get().fetchTransactions(),
      ]);
    } catch {
      // Non-critical — silently ignore sync failures
    }
  },

  // ── Game purchase (integrated with billing) ────────────────────────────

  initiateGamePurchase: async (gameId, paymentMethodId, useWallet, saveCard) => {
    try {
      const body: Record<string, unknown> = { useWallet: useWallet ?? true };
      if (paymentMethodId) body.paymentMethodId = paymentMethodId;
      if (saveCard !== undefined) body.saveCard = saveCard;
      const { data } = await api.post(`/games/${gameId}/purchase`, body);
      if (data.requiresPayment) {
        return { clientSecret: data.clientSecret };
      }
      return { completed: true };
    } catch (e: any) {
      const msg = e.response?.data?.error || e.response?.data?.message || 'Erreur d\'achat';
      set({ error: msg });
      return { failed: true, message: msg };
    }
  },
}));
